/**
 * UserForm — create/edit dialog body for `/admin/users`.
 *
 * In create mode the user's email + role + name are editable. In edit mode
 * the email is locked server-side (the backend PUT doesn't even accept it);
 * the form mirrors that by disabling the input.
 */

import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type AdminRole = 'admin' | 'operator' | 'viewer';

export interface UserFormValue {
  email: string;
  name: string;
  role: AdminRole;
  active: boolean;
}

export const DEFAULT_USER_FORM: UserFormValue = {
  email: '',
  name: '',
  role: 'operator',
  active: true,
};

interface Props {
  mode: 'create' | 'edit';
  initial?: UserFormValue;
  submitting?: boolean;
  onSubmit: (next: UserFormValue) => void;
  onCancel: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ROLE_OPTIONS: AdminRole[] = ['admin', 'operator', 'viewer'];

export function UserForm({ mode, initial, submitting, onSubmit, onCancel }: Props) {
  const [form, setForm] = useState<UserFormValue>(initial ?? DEFAULT_USER_FORM);
  const [emailError, setEmailError] = useState<string | null>(null);

  function patch<K extends keyof UserFormValue>(key: K, value: UserFormValue[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleEmailChange(next: string) {
    patch('email', next);
    if (mode === 'edit') return;
    if (next === '' || EMAIL_RE.test(next)) {
      setEmailError(null);
    } else {
      setEmailError('Email inválido.');
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mode === 'create' && !EMAIL_RE.test(form.email)) {
      setEmailError('Email inválido.');
      return;
    }
    onSubmit(form);
  }

  const disabled =
    submitting || (mode === 'create' && (form.email.length === 0 || emailError !== null));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="user-email">Email</Label>
        <Input
          id="user-email"
          aria-label="Email"
          type="email"
          value={form.email}
          onChange={(e) => handleEmailChange(e.target.value)}
          disabled={mode === 'edit'}
          maxLength={255}
        />
        {emailError ? (
          <p className="text-sm text-destructive" role="alert">
            {emailError}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="user-name">Nome</Label>
        <Input
          id="user-name"
          aria-label="Nome"
          value={form.name}
          onChange={(e) => patch('name', e.target.value)}
          maxLength={255}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="user-role">Role</Label>
        <select
          id="user-role"
          aria-label="Role"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={form.role}
          onChange={(e) => patch('role', e.target.value as AdminRole)}
        >
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      {mode === 'edit' ? (
        <div className="flex items-center gap-2">
          <input
            id="user-active"
            aria-label="Ativo"
            type="checkbox"
            checked={form.active}
            onChange={(e) => patch('active', e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          <Label htmlFor="user-active">Ativo</Label>
        </div>
      ) : null}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={!!submitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={disabled}>
          {mode === 'create' ? 'Criar usuário' : 'Salvar alterações'}
        </Button>
      </div>
    </form>
  );
}
